import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Building blocks shared by the Create PR and Create PO pages: a two-column editor (the form, and a
 * summary panel beside it from `lg` up and under it below that), numbered section cards, side cards
 * and the "before you submit" checklist. They only lay things out: every control and every check
 * they show belongs to the page.
 */
export const EDITOR_COLORS = {
  text: '#32363a',
  muted: '#6a6d70',
  border: '#cfd8dc',
  divider: '#e5e9eb',
  blue: '#0a6ed1',
  blueText: '#085bb0',
  blueTint: '#e8f1fb',
  green: '#107e3e',
  amber: '#a84b00',
  amberTint: '#fef3e8',
  red: '#bb0000',
  subtle: '#f3f6f8',
} as const;

/** Style of a native input or select inside an EditorField (the pages keep their native controls). */
export const editorControlStyle: React.CSSProperties = {
  width: '100%',
  height: 36,
  boxSizing: 'border-box',
  padding: '0 10px',
  border: '1px solid #89919a',
  borderRadius: 3,
  background: '#ffffff',
  color: EDITOR_COLORS.text,
  fontSize: 13,
  fontFamily: 'inherit',
};

/** A neutral outlined button: the theme's gold outline is too light for text. */
export const secondaryButtonSx = {
  color: EDITOR_COLORS.text,
  borderColor: '#89919a',
  bgcolor: '#ffffff',
  '&:hover': { borderColor: EDITOR_COLORS.text, bgcolor: '#f5f7f8' },
} as const;

/** An outlined button in the shell's blue, for "add" actions next to the primary one. */
export const addButtonSx = {
  color: EDITOR_COLORS.blueText,
  borderColor: EDITOR_COLORS.blue,
  bgcolor: '#ffffff',
  '&:hover': { borderColor: EDITOR_COLORS.blueText, bgcolor: EDITOR_COLORS.blueTint },
} as const;

export const chipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 24,
  px: 1,
  border: `1px solid ${EDITOR_COLORS.border}`,
  borderRadius: '12px',
  bgcolor: '#ffffff',
  fontSize: 12,
  color: EDITOR_COLORS.text,
  whiteSpace: 'nowrap',
} as const;

/** A label above a control, with an optional hint under it. */
export const EditorField: React.FC<{ label: React.ReactNode; htmlFor: string; hint?: React.ReactNode; children: React.ReactNode }> = ({
  label,
  htmlFor,
  hint,
  children,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Box component="label" htmlFor={htmlFor} sx={{ display: 'block', mb: 0.75, fontSize: 12, fontWeight: 600, color: EDITOR_COLORS.text }}>
      {label}
    </Box>
    {children}
    {hint != null && <Box sx={{ mt: 0.5, fontSize: 12, color: EDITOR_COLORS.muted }}>{hint}</Box>}
  </Box>
);

export const EditorLayout: React.FC<{ main: React.ReactNode; side: React.ReactNode; sideLabel?: string }> = ({
  main,
  side,
  sideLabel = 'Summary and checks',
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
    <Box sx={{ flex: '999 1 640px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>{main}</Box>
    <Box
      component="aside"
      aria-label={sideLabel}
      sx={{
        flex: '1 1 300px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: { lg: 'sticky' },
        top: { lg: 8 },
      }}
    >
      {side}
    </Box>
  </Box>
);

/** A numbered section of the form: a white card with its heading and, on the right, its own buttons. */
export const EditorSection: React.FC<{
  id: string;
  title: React.ReactNode;
  number?: number;
  actions?: React.ReactNode;
  note?: React.ReactNode;
  disableBodyPadding?: boolean;
  children: React.ReactNode;
}> = ({ id, title, number, actions, note, disableBodyPadding, children }) => (
  <Paper component="section" aria-labelledby={id} elevation={0} sx={{ bgcolor: '#ffffff', overflow: 'hidden' }}>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      useFlexGap
      spacing={1}
      sx={{ px: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 }, pb: 1.5 }}
    >
      <Typography
        component="h2"
        id={id}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, m: 0, fontSize: 15, fontWeight: 600, color: EDITOR_COLORS.text }}
      >
        {number != null && (
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: EDITOR_COLORS.blueTint,
              color: EDITOR_COLORS.blueText,
              fontSize: 12,
              fontWeight: 700,
              flex: 'none',
            }}
          >
            {number}
          </Box>
        )}
        {title}
        {note != null && (
          <Box component="span" sx={{ fontSize: 13, fontWeight: 400, color: EDITOR_COLORS.muted }}>
            {note}
          </Box>
        )}
      </Typography>
      {actions}
    </Stack>
    <Box sx={disableBodyPadding ? undefined : { px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>{children}</Box>
  </Paper>
);

/** A card in the summary panel. */
export const SideCard: React.FC<{ id: string; title: React.ReactNode; children: React.ReactNode }> = ({ id, title, children }) => (
  <Paper component="section" aria-labelledby={id} elevation={0} sx={{ p: 2, bgcolor: '#ffffff' }}>
    <Typography component="h2" id={id} sx={{ m: 0, mb: 1, fontSize: 14, fontWeight: 600, color: EDITOR_COLORS.text }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

/** A label and its value in the summary panel. */
export const SideFact: React.FC<{ label: React.ReactNode; children: React.ReactNode; strong?: boolean }> = ({ label, children, strong }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5, py: 0.25 }}>
    <Typography component="span" sx={{ fontSize: 13, color: EDITOR_COLORS.muted }}>
      {label}
    </Typography>
    <Typography component="span" sx={{ fontSize: strong ? 15 : 13, fontWeight: strong ? 700 : 600, color: EDITOR_COLORS.text, textAlign: 'right' }}>
      {children}
    </Typography>
  </Box>
);

/**
 * What is still missing before Submit, from the page's own validation (the same messages Submit shows),
 * or "Ready to submit" when nothing is.
 */
export const SubmitChecklist: React.FC<{ problems: string[]; readyText?: string; max?: number; hint?: React.ReactNode }> = ({
  problems,
  readyText = 'Ready to submit',
  max = 6,
  hint,
}) => {
  if (!problems.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: EDITOR_COLORS.green, fontWeight: 600, fontSize: 13 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} aria-hidden="true" />
        <span>{readyText}</span>
      </Box>
    );
  }
  const shown = problems.slice(0, max);
  const more = problems.length - shown.length;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: EDITOR_COLORS.amber, fontWeight: 600, fontSize: 13, mb: 0.75 }}>
        <WarningAmberIcon sx={{ fontSize: 18 }} aria-hidden="true" />
        <span>{problems.length === 1 ? '1 thing to fix before you submit' : `${problems.length} things to fix before you submit`}</span>
      </Box>
      <Box
        component="ul"
        sx={{ m: 0, p: '8px 10px', listStyle: 'none', borderRadius: '6px', bgcolor: EDITOR_COLORS.amberTint, display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        {shown.map((p, i) => (
          <Box component="li" key={`${i}-${p}`} sx={{ fontSize: 12.5, lineHeight: 1.4, color: EDITOR_COLORS.text }}>
            {p}
          </Box>
        ))}
        {more > 0 && (
          <Box component="li" sx={{ fontSize: 12.5, color: EDITOR_COLORS.muted }}>
            and {more} more
          </Box>
        )}
      </Box>
      {hint && <Box sx={{ mt: 0.75, fontSize: 12, color: EDITOR_COLORS.muted }}>{hint}</Box>}
    </Box>
  );
};

/** The document's status as a small pill (Draft, Pending, Approved, Rejected...). */
export const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const tone = s.includes('reject') || s.includes('cancel')
    ? { bg: '#ffebeb', fg: EDITOR_COLORS.red }
    : s.includes('approv') || s.includes('posted') || s.includes('closed')
      ? { bg: '#e7f4ec', fg: '#0d6b34' }
      : s.includes('pending') || s.includes('submit') || s.includes('progress')
        ? { bg: EDITOR_COLORS.blueTint, fg: EDITOR_COLORS.blueText }
        : { bg: '#eef1f3', fg: EDITOR_COLORS.text };
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', height: 22, px: 1, borderRadius: '11px', bgcolor: tone.bg, color: tone.fg, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {status}
    </Box>
  );
};
